<script setup>
import { onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { X } from 'lucide-vue-next'
import { useToastStore } from '@/stores/toasts.js'

const AUTO_DISMISS_MS = 6000

const { t, te } = useI18n()
const toasts = useToastStore()

// item.id -> setTimeout handle. Keyed by id, never by array position: `toasts.items`
// reshuffles every time an earlier toast is removed, so an implementation that captured
// an index instead of an id would fire the wrong toast's timer (or none at all) once that
// happens. See ToastStack.test.js's "dismissing one of several toasts..." tests.
const timers = new Map()

// A server-controlled `code` must never be trusted as a locale key outright — an unmapped
// code would otherwise render vue-i18n's literal missing-key fallback (e.g.
// "toast.sql_injection_detected") straight into the DOM, which is server-controlled text
// reaching the user by another name. te() gates every code before t() ever sees it (same
// pattern as ProfileView's safeCode()); an unrecognised code degrades to toast.unknown.
function messageFor(item) {
  const key = `toast.${item.code}`
  return te(key) ? t(key) : t('toast.unknown')
}

function clearTimer(id) {
  const handle = timers.get(id)
  if (handle !== undefined) {
    globalThis.clearTimeout(handle)
    timers.delete(id)
  }
}

function dismiss(id) {
  // Clearing the pending auto-dismiss timer here is what stops a manual dismiss from
  // leaving a stale timeout behind that fires ~6s later and calls store.dismiss() again
  // for an id already gone — harmless today only because the store's ids are never
  // reused, but still a real leaked timer and a real spurious call.
  clearTimer(id)
  toasts.dismiss(id)
}

watch(
  () => toasts.items.map((item) => item.id),
  (ids) => {
    for (const id of ids) {
      if (timers.has(id)) continue
      timers.set(
        id,
        globalThis.setTimeout(() => {
          timers.delete(id)
          toasts.dismiss(id)
        }, AUTO_DISMISS_MS)
      )
    }
  },
  { immediate: true }
)

// A component-scoped setTimeout is not tied to Vue's reactive scope and keeps running
// after unmount — without this, a torn-down instance (e.g. after a route change remounts
// the shell) could still fire later and dismiss a toast a *different* mounted instance
// never scheduled.
onUnmounted(() => {
  for (const handle of timers.values()) globalThis.clearTimeout(handle)
  timers.clear()
})
</script>

<template>
  <!--
    Always-mounted aria-live region, like OfflineBanner: toasts are added into it, they are
    never the region's own insertion, so additions are reliably announced. `role="status"`
    lives on each toast item instead (not on this wrapper), which is also what keeps
    `findAll('[role="status"]')` at zero while the list is empty.
  -->
  <div
    aria-live="polite"
    class="pointer-events-none fixed inset-x-0 bottom-24 z-50 flex flex-col items-center gap-2 px-5"
  >
    <div
      v-for="item in toasts.items"
      :key="item.id"
      role="status"
      class="pointer-events-auto flex w-full max-w-content items-center gap-3 rounded-card bg-ink px-4 py-3 text-cream"
    >
      <p class="flex-1 text-sm">
        {{ messageFor(item) }}
      </p>
      <button
        data-testid="dismiss"
        type="button"
        class="p-1"
        :aria-label="t('common.dismiss')"
        @click="dismiss(item.id)"
      >
        <X
          class="size-4"
          aria-hidden="true"
        />
      </button>
    </div>
  </div>
</template>
